import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import {
	useAssignCaseWorkersMutation,
	useReturnFromICMutation,
} from '@/api/member-management/admissionRequest';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useCloseAllMembershipMutation } from '@/api/family-management/familyApi';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import dayjs from 'dayjs';

interface Props {
	open: boolean;
	onClose: () => void;
}

const CloseAllMemberShipDialog = (props: Props) => {
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [closeAllMembership] = useCloseAllMembershipMutation();
	const { familyId } = useParams();

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		dateOfExitService: Yup.string()
			.required(t('errorMessage.required'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};
	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await closeAllMembership({
			...data,
			id: familyId,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFamilySuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle variant='h5'>{t('family.closeAllMemberShip')}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={handleClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '12px' }}>
					{t('family.closeAllMemberShipText')}
				</Typography>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='dateOfExitService'
									label={t('member.dateExitService') + '*'}
								/>
							</Grid>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									name='exitReason'
									label={t('family.exitReason')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
					{t('button.cancel')}
				</Button>
				<Button
					type='submit'
					variant='contained'
					onClick={methods.handleSubmit(onSubmit)}
				>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CloseAllMemberShipDialog;
